// =============================================================================
// DRIVE EXPORT STATE — the DriveSaveButton's pure decision logic, extracted so
// it's directly unit-testable without standing up a component render harness
// (mirrors this app's own house pattern — goodbyeRoute.ts / homeRouteGate.ts /
// printDocumentTitle.ts are all small pure modules a component imports rather
// than inlining the logic in JSX).
//
// Design brief: docs/design/drive-export-2026-07-05.md.
// =============================================================================

/** Every honest state the button can be in. 'checking' is the initial GET
 *  /exports/drive/status round-trip; 'not-connected' means no live grant;
 *  'idle'/'saving'/'saved' are the connected happy path; 'error' covers both
 *  a plain failure and the reconnect-required shape (see classifyDriveError). */
export type DriveButtonPhase =
  | 'checking'
  | 'not-connected'
  | 'idle'
  | 'saving'
  | 'saved'
  | 'error';

export type DriveErrorKind =
  | 'reconnect'
  | 'not_connected'
  | 'entitlement_required'
  | 'connection_broken'
  | 'unknown';

export interface ClassifiedDriveError {
  kind: DriveErrorKind;
  /** Mira's own first-person line for this failure — never a raw error
   *  string or stack trace surfaced to the user. */
  message: string;
}

/** Classify a POST /exports/drive failure into an honest, Mira-voiced
 *  message. Mirrors the server's own error shapes exactly (exports.routes.ts):
 *  { error: 'reconnect' | 'not_connected', hint } on 409, { error:
 *  'entitlement_required', capability, tier, reason, hint } on 402
 *  (TEMPLATES-P2 §6 — a premium template family export without the
 *  template_library capability), or anything else (network failure, 5xx,
 *  malformed body) degrades to 'unknown'. Never fabricates a more specific
 *  reason than the server actually gave. */
export function classifyDriveError(body: unknown): ClassifiedDriveError {
  const err =
    typeof body === 'object' && body !== null && 'error' in body
      ? (body as { error?: unknown }).error
      : undefined;

  if (err === 'reconnect') {
    return {
      kind: 'reconnect',
      message: "Your Google Drive connection expired — I'll need you to reconnect to keep saving.",
    };
  }
  if (err === 'not_connected') {
    return {
      kind: 'not_connected',
      message: 'Connect Google Drive first, then I can save this there.',
    };
  }
  if (err === 'entitlement_required') {
    return {
      kind: 'entitlement_required',
      message:
        "Start your trial to unlock the full template library — your résumé stays exactly as true either way.",
    };
  }
  return {
    kind: 'unknown',
    message: "I couldn't save that to Drive — try again in a moment.",
  };
}

/** Whether the button should offer a "Connect Google Drive" affordance
 *  instead of "Save to Drive" — true for the not-connected phase, and for an
 *  error classified as needing reconnection (the SAME affordance covers both:
 *  a fresh connect and a reconnect are the same OAuth flow re-entered). */
export function needsConnectAffordance(
  phase: DriveButtonPhase,
  errorKind: DriveErrorKind | null,
): boolean {
  if (phase === 'not-connected') return true;
  if (phase === 'error' && (errorKind === 'reconnect' || errorKind === 'not_connected')) return true;
  return false;
}

/** The button's own label for a given phase — a single source of truth so
 *  the component never hand-writes phase-to-copy mapping inline (and so this
 *  mapping is itself testable independent of JSX). */
export function driveButtonLabel(
  phase: DriveButtonPhase,
  errorKind: DriveErrorKind | null,
): string {
  switch (phase) {
    case 'checking':
      return 'Checking Drive…';
    case 'not-connected':
      return 'Connect Google Drive';
    case 'idle':
      return 'Save to Drive';
    case 'saving':
      return 'Saving…';
    case 'saved':
      return 'Open in Drive';
    case 'error':
      if (needsConnectAffordance(phase, errorKind)) return 'Reconnect Google Drive';
      // A retry alone can never fix a 402 — the label says so honestly
      // rather than inviting a doomed "Try again" tap (TEMPLATES-P2 §6).
      if (errorKind === 'entitlement_required') return 'Go to billing';
      // SEC-4 — the loop-breaker has tripped (see isDriveReconnectLoopTripped
      // below): neither a retry nor another reconnect can fix a server-side
      // storage bug, so the label stops inviting either action.
      if (errorKind === 'connection_broken') return 'Drive unavailable';
      return 'Try again';
    default:
      return 'Save to Drive';
  }
}

// =============================================================================
// SEC-4 · A-P4 — the Drive save/connect LOOP-BREAKER (Phase A,
// docs/qa/FOUNDATION-FIX-PLAN-2026-07-09.md). Evidence
// (docs/qa/evidence-2026-07-09-live-test.md, D-1): before the SEC-1..SEC-3
// server fix, every Drive connect succeeded at Google yet every SAVE still
// 409'd (a write/read key mismatch) — and the button's own honest
// 'reconnect' affordance kept inviting the user to redo an OAuth dance that
// could never fix a server-side storage bug, an infinite, user-visible retry
// loop (the tail: connect → save 409 → reconnect → save 409 AGAIN → "the
// loop closes"). SEC-1..SEC-3 fix the ROOT CAUSE; this is the client-side
// backstop for any future regression of the same CLASS: once a connect has
// been confirmed at least once and a SECOND consecutive reconnect-shaped
// failure lands, continuing to offer "reconnect" is dishonest (it cannot
// help) — the button goes terminal instead and says so plainly.
//
// Every function below is PURE (explicit state in, next state out) — the
// component owns the actual React state + sessionStorage plumbing, mirroring
// every other function in this file and this app's house "pure module, thin
// component" idiom (exportFlowState.ts / storyTour.ts).
// =============================================================================

export interface DriveConnectAttemptState {
  /** Has THIS session ever confirmed a live Drive connection (a driveStatus()
   *  call that returned connected:true)? A 409 before any connect has ever
   *  been confirmed is just the ordinary not-connected-yet state — never
   *  counted, never enough on its own to trip the breaker. */
  everConnected: boolean;
  /** Consecutive reconnect-shaped (409 'reconnect' | 'not_connected') SAVE
   *  failures observed since everConnected first became true, or since the
   *  last successful save (whichever is more recent) — NOT reset merely by
   *  clicking "Reconnect" (re-running the OAuth dance is exactly the
   *  ineffective action this counter exists to eventually stop inviting). */
  reconnectFailureCount: number;
}

export const INITIAL_DRIVE_CONNECT_ATTEMPT_STATE: DriveConnectAttemptState = {
  everConnected: false,
  reconnectFailureCount: 0,
};

/** Call once a live grant is confirmed (driveStatus() → connected:true) —
 *  marks that a real connect has happened, so a LATER 409 is evaluated as a
 *  genuine regression rather than mistaken for "hasn't connected yet."
 *  Idempotent. */
export function markDriveEverConnected(
  state: DriveConnectAttemptState,
): DriveConnectAttemptState {
  if (state.everConnected) return state;
  return { ...state, everConnected: true };
}

/** A successful save is real proof the connection works end-to-end — clears
 *  the failure count so a later, unrelated blip is never wrongly compounded
 *  with an old, already-resolved one. */
export function resetDriveReconnectFailures(
  state: DriveConnectAttemptState,
): DriveConnectAttemptState {
  return { ...state, reconnectFailureCount: 0 };
}

/** Record one reconnect-shaped (409 'reconnect' | 'not_connected') save
 *  failure. A failure BEFORE any connect has ever succeeded is left
 *  uncounted — that is the ordinary not-connected-yet state, not a loop. */
export function recordDriveReconnectFailure(
  state: DriveConnectAttemptState,
): DriveConnectAttemptState {
  if (!state.everConnected) return state;
  return { ...state, reconnectFailureCount: state.reconnectFailureCount + 1 };
}

/** SEC-4's trip point: a SECOND consecutive reconnect-shaped failure after a
 *  real connect has been confirmed. */
export function isDriveReconnectLoopTripped(state: DriveConnectAttemptState): boolean {
  return state.reconnectFailureCount >= 2;
}

/** Escalate an ordinary reconnect/not_connected classification to the
 *  terminal 'connection_broken' kind once the loop-breaker has tripped.
 *  Every OTHER classified kind (entitlement_required, unknown) passes
 *  through unchanged — the loop-breaker only ever concerns the
 *  reconnect-shaped failures it is specifically counting. */
export function escalateIfLoopTripped(
  classified: ClassifiedDriveError,
  attemptState: DriveConnectAttemptState,
): ClassifiedDriveError {
  const isReconnectShaped = classified.kind === 'reconnect' || classified.kind === 'not_connected';
  if (isReconnectShaped && isDriveReconnectLoopTripped(attemptState)) {
    return {
      kind: 'connection_broken',
      message:
        "The connection is broken on our side — this is a bug, not your account. " +
        "Reconnecting won't fix it, so I've stopped asking — try again later.",
    };
  }
  return classified;
}

// =============================================================================
// sessionStorage persistence for the loop-breaker's attempt state.
// Deliberately sessionStorage, not localStorage (mirrors storyTour.ts's
// hasWindow()/try-catch discipline, but tab-scoped rather than
// permanent): the whole point is surviving the SAME-TAB full-page OAuth
// round trip (handleConnect navigates via window.location.href, Google
// redirects back to the same path) — an in-memory-only counter resets to 0
// on every remount and could never observe "the second failure since we
// last knew we were connected." A brand-new tab starting fresh (one extra
// allowed attempt) is an acceptable, still-bounded trade-off — never a
// permanent cross-session lockout once the underlying bug is truly fixed.
// =============================================================================

const ATTEMPT_STORAGE_KEY = 'tl-drive-connect-attempts';

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

export function readDriveConnectAttemptStorage(): DriveConnectAttemptState {
  if (!hasSessionStorage()) return INITIAL_DRIVE_CONNECT_ATTEMPT_STATE;
  try {
    const raw = window.sessionStorage.getItem(ATTEMPT_STORAGE_KEY);
    if (!raw) return INITIAL_DRIVE_CONNECT_ATTEMPT_STATE;
    const parsed = JSON.parse(raw) as Partial<DriveConnectAttemptState>;
    return {
      everConnected: parsed.everConnected === true,
      reconnectFailureCount:
        typeof parsed.reconnectFailureCount === 'number' && parsed.reconnectFailureCount >= 0
          ? parsed.reconnectFailureCount
          : 0,
    };
  } catch {
    return INITIAL_DRIVE_CONNECT_ATTEMPT_STATE;
  }
}

export function writeDriveConnectAttemptStorage(state: DriveConnectAttemptState): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(ATTEMPT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Best-effort only, same posture as storyTour.ts's writeStoryTourStorage
    // — worst case the loop-breaker forgets across a reload and allows a
    // couple of extra attempts, never a crash.
  }
}
